import { motion } from "framer-motion";
import {
  BadgeCheck, Coins, TrendingUp, Network, Rocket, Check,
  Target, Search, Handshake, Users, MapPinned, Award, ArrowDown,
} from "lucide-react";
import { Chapter, FadeUp, MaskedLine, EASE } from "@/components/Reveal";
import { scrollToAnchor } from "@/components/Header";
import { CandidatureForm } from "@/components/CandidatureForm";

const IMG_RECRUTEMENT =
  "https://images.unsplash.com/photo-1782829243963-3a2dee566511?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmFsJTIwYnVpbGRpbmclMjB3b29kJTIwc3RvbmUlMjBkZXRhaWx8ZW58MHx8fHwxNzg5NzE3MzA5fDA&ixlib=rb-4.1.0&q=85";

const PROPOSITIONS = [
  {
    icon: BadgeCheck,
    title: "Statut cadre",
    desc: "Un poste à responsabilités avec une véritable autonomie commerciale sur votre secteur.",
    testid: "prop-statut-cadre",
  },
  {
    icon: Coins,
    title: "Rémunération attractive",
    desc: "Une rémunération fixe attractive, cohérente avec le niveau de responsabilité et l'expérience du candidat.",
    testid: "prop-remuneration",
  },
  {
    icon: TrendingUp,
    title: "Commissions importantes",
    desc: "Un dispositif de rémunération variable destiné à récompenser fortement la performance commerciale.",
    testid: "prop-commissions",
  },
  {
    icon: Network,
    title: "Potentiel de développement",
    desc: "Développez votre portefeuille, construisez votre réseau et participez directement à la croissance d'EQUORIS.",
    testid: "prop-developpement",
  },
  {
    icon: Rocket,
    title: "Un projet entrepreneurial",
    desc: "Vous rejoignez une entreprise en développement dans laquelle les commerciaux participent concrètement à la construction de la marque et de son réseau.",
    testid: "prop-entrepreneurial",
  },
];

const MISSIONS = [
  { icon: Target, title: "Prospecter", desc: "Identifier et développer de nouveaux clients professionnels.", testid: "mission-prospecter" },
  { icon: Search, title: "Comprendre", desc: "Analyser les besoins des artisans, entreprises et professionnels du bâtiment.", testid: "mission-comprendre" },
  { icon: Handshake, title: "Conseiller", desc: "Identifier avec EQUORIS les solutions et familles de matériaux adaptées.", testid: "mission-conseiller" },
  { icon: TrendingUp, title: "Développer", desc: "Construire et développer votre portefeuille clients.", testid: "mission-developper" },
  { icon: Users, title: "Fidéliser", desc: "Créer une relation commerciale durable.", testid: "mission-fideliser" },
  { icon: Award, title: "Représenter EQUORIS", desc: "Développer la présence et la notoriété de la marque sur votre secteur.", testid: "mission-representer" },
];

const PROFIL = [
  "Véritable tempérament commercial",
  "Goût de la prospection et du terrain",
  "Autonomie",
  "Capacité de négociation",
  "Sens de la relation client",
  "Capacité à développer un portefeuille",
  "Organisation et rigueur",
  "Connaissance du BTP ou du négoce appréciée",
  "Expérience commerciale B2B appréciée",
];

export default function RecrutementPage() {
  return (
    <main data-testid="recrutement-page">
      {/* HERO */}
      <section className="relative bg-ivoire grain overflow-hidden pt-[72px]" data-testid="recrutement-hero">
        <div
          className="absolute -top-20 -left-20 w-[360px] h-[360px] rounded-t-full bg-sagelight/70 -rotate-90"
          aria-hidden="true"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.1 }}
                className="text-xs uppercase tracking-[0.3em] font-semibold text-teal mb-8"
              >
                Recrutement — Commerciaux terrain
              </motion.p>
              <h1
                className="font-display font-medium text-petrol text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.04] tracking-tight"
                data-testid="recrutement-title"
              >
                <MaskedLine delay={0.25}>Construisons</MaskedLine>
                <MaskedLine delay={0.42} className="pb-3">
                  <span className="italic text-teal">EQUORIS ensemble.</span>
                </MaskedLine>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
                className="mt-7 text-base sm:text-lg text-mute max-w-xl leading-relaxed"
              >
                <span className="text-encre font-medium">
                  Rejoignez une nouvelle génération du négoce de matériaux et participez à
                  notre ambition : faire d'EQUORIS un acteur de référence du marché.
                </span>
                <br />
                Vous savez développer un portefeuille, créer une relation commerciale durable
                et transformer les besoins chantier en opportunités ? Nous voulons vous
                rencontrer.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
                className="mt-9 flex flex-col sm:flex-row gap-4"
              >
                <button
                  onClick={() => scrollToAnchor("candidature")}
                  data-testid="recrutement-cta-rejoindre"
                  className="rounded-full bg-petrol text-ivoire text-sm font-semibold px-7 py-4 inline-flex items-center justify-center gap-2 transition-all duration-300 hover:bg-teal hover:-translate-y-0.5"
                >
                  Rejoindre EQUORIS
                </button>
                <button
                  onClick={() => scrollToAnchor("candidature")}
                  data-testid="recrutement-cta-candidature"
                  className="rounded-full border border-petrol/25 text-petrol text-sm font-semibold px-7 py-4 inline-flex items-center justify-center gap-2 transition-all duration-300 hover:border-teal hover:text-teal hover:-translate-y-0.5"
                >
                  Déposer ma candidature
                  <ArrowDown className="w-4 h-4" />
                </button>
              </motion.div>
            </div>
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 60, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1.3, delay: 0.55, ease: EASE }}
              >
                <div className="rounded-t-[999px] rounded-b-[2rem] overflow-hidden shadow-2xl shadow-petrol/20 border border-petrol/10">
                  <img
                    src={IMG_RECRUTEMENT}
                    alt="Architecture contemporaine en bois courbe"
                    className="w-full h-[380px] sm:h-[460px] object-cover"
                    loading="eager"
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* NOUS RECRUTONS */}
      <section className="bg-creme py-20 lg:py-28" data-testid="recrutement-poste">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Chapter num="01" label="Nous recrutons" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <FadeUp className="lg:col-span-5">
              <h2 className="font-display font-medium text-petrol text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight">
                Commerciaux terrain — Statut cadre
              </h2>
            </FadeUp>
            <FadeUp delay={0.15} className="lg:col-span-7 space-y-5">
              <p className="text-base text-mute leading-relaxed">
                EQUORIS développe son organisation commerciale et recherche des profils
                capables de prospecter, développer et fidéliser une clientèle professionnelle
                dans l'univers du bâtiment, de la construction et de la rénovation.
              </p>
              <p className="text-base text-mute leading-relaxed">
                Nous recherchons avant tout des tempéraments commerciaux : autonomie, énergie,
                goût du terrain, capacité à créer une relation de confiance et envie de
                construire un portefeuille sur la durée.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* CE QUE NOUS PROPOSONS */}
      <section className="bg-ivoire py-20 lg:py-32" data-testid="recrutement-propositions">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Chapter num="02" label="Ce que nous proposons" />
          <FadeUp>
            <h2 className="font-display font-medium text-petrol text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight mb-14 lg:mb-20 max-w-3xl">
              Un cadre à la hauteur de votre ambition.
            </h2>
          </FadeUp>
          <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
            {PROPOSITIONS.map(({ icon: Icon, title, desc, testid }, i) => (
              <FadeUp
                key={testid}
                delay={i * 0.08}
                className={i < 2 ? "md:col-span-3" : "md:col-span-2"}
              >
                <div
                  data-testid={testid}
                  className="group h-full bg-white rounded-3xl border border-petrol/10 p-8 transition-all duration-300 hover:-translate-y-1.5 hover:border-teal/40 hover:shadow-xl hover:shadow-petrol/5"
                >
                  <span className="w-12 h-12 rounded-t-full rounded-b-xl bg-sagelight flex items-center justify-center mb-8 transition-colors duration-300 group-hover:bg-teal/15">
                    <Icon className="w-5 h-5 text-teal" />
                  </span>
                  <h3 className="font-display font-medium text-petrol text-2xl mb-3">{title}</h3>
                  <p className="text-sm text-mute leading-relaxed">{desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTE */}
      <section className="bg-petrol text-ivoire py-20 lg:py-32 relative overflow-hidden grain" data-testid="recrutement-manifeste">
        <div
          className="absolute -top-24 -right-24 w-[340px] h-[340px] rounded-t-full bg-teal/15 rotate-90"
          aria-hidden="true"
        />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-8 lg:px-16">
          <Chapter num="03" label="Le projet" dark />
          <FadeUp>
            <h2 className="font-display font-medium text-3xl sm:text-4xl lg:text-6xl leading-tight tracking-tight mb-10">
              Aidez-nous à devenir{" "}
              <span className="italic text-sage">un acteur de référence.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.15} className="space-y-6 max-w-3xl">
            <p className="text-base sm:text-lg text-ivoire/80 leading-relaxed">
              Nous ne recherchons pas simplement des vendeurs.
            </p>
            <p className="text-base sm:text-lg text-ivoire/80 leading-relaxed">
              Nous recherchons des femmes et des hommes capables de développer leur marché, de
              comprendre les professionnels du bâtiment et de faire grandir EQUORIS sur leur
              territoire.
            </p>
            <p className="text-base sm:text-lg text-ivoire/80 leading-relaxed">
              Votre réussite commerciale contribuera directement à la réussite de
              l'entreprise.
            </p>
            <p className="font-display italic text-xl sm:text-2xl text-sage pt-6 border-t border-ivoire/15">
              « Notre ambition : construire avec nos équipes commerciales l'un des acteurs de
              référence du marché. »
            </p>
          </FadeUp>
        </div>
      </section>

      {/* VOTRE MISSION */}
      <section className="bg-creme py-20 lg:py-32" data-testid="recrutement-mission">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Chapter num="04" label="Votre mission" />
          <FadeUp>
            <h2 className="font-display font-medium text-petrol text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight mb-14 lg:mb-20">
              Un parcours, six engagements.
            </h2>
          </FadeUp>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {MISSIONS.map(({ icon: Icon, title, desc, testid }, i) => (
              <FadeUp key={testid} delay={i * 0.08}>
                <div
                  data-testid={testid}
                  className="group h-full bg-white rounded-3xl border border-petrol/10 p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-teal/40 hover:shadow-xl hover:shadow-petrol/5"
                >
                  <div className="flex items-center justify-between mb-7">
                    <span className="w-11 h-11 rounded-t-full rounded-b-xl bg-sagelight flex items-center justify-center transition-colors duration-300 group-hover:bg-teal/15">
                      <Icon className="w-5 h-5 text-teal" />
                    </span>
                    <span className="font-display italic text-2xl text-petrol/25">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-petrol mb-2.5">{title}</h3>
                  <p className="text-sm text-mute leading-relaxed">{desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* PROFIL RECHERCHÉ */}
      <section className="bg-ivoire py-20 lg:py-32" data-testid="recrutement-profil">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Chapter num="05" label="Profil recherché" />
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <FadeUp className="lg:col-span-5">
              <h2 className="font-display font-medium text-petrol text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight mb-8">
                Le tempérament avant le diplôme.
              </h2>
              <p className="text-base text-mute leading-relaxed mb-4">
                Une excellente expérience commerciale peut être privilégiée à un parcours
                académique traditionnel.
              </p>
              <p className="text-base text-mute leading-relaxed">
                La mission étant itinérante, le permis B est requis.
              </p>
            </FadeUp>
            <FadeUp delay={0.15} className="lg:col-span-7">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4" data-testid="profil-list">
                {PROFIL.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-0.5 w-6 h-6 rounded-full bg-sagelight flex items-center justify-center shrink-0">
                      <Check className="w-3.5 h-3.5 text-teal" />
                    </span>
                    <span className="text-sm text-encre/85 leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* POURQUOI MAINTENANT */}
      <section className="bg-sagelight py-20 lg:py-28" data-testid="recrutement-pourquoi">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-16 text-center">
          <FadeUp>
            <MapPinned className="w-8 h-8 text-teal mx-auto mb-8" />
            <h2 className="font-display font-medium text-petrol text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight mb-8">
              Le meilleur moment pour nous rejoindre ?{" "}
              <span className="italic text-teal">Maintenant.</span>
            </h2>
            <p className="text-base sm:text-lg text-mute leading-relaxed max-w-3xl mx-auto mb-8">
              EQUORIS est en phase de développement. Les commerciaux qui nous rejoignent
              aujourd'hui participeront directement à la création de notre organisation
              commerciale, au développement des secteurs et à la construction de relations
              clients durables.
            </p>
            <p className="font-display italic text-xl sm:text-2xl text-petrol">
              « Vous ne rejoignez pas seulement un poste. Vous contribuez à construire le
              projet. »
            </p>
          </FadeUp>
        </div>
      </section>

      {/* CANDIDATURE */}
      <section id="candidature" className="bg-creme py-20 lg:py-32 scroll-mt-16" data-testid="candidature-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <Chapter num="06" label="Candidature" />
              <FadeUp>
                <h2 className="font-display font-medium text-petrol text-3xl sm:text-4xl leading-tight tracking-tight mb-6">
                  Déposez votre candidature.
                </h2>
                <p className="text-base text-mute leading-relaxed">
                  Quelques informations suffisent pour démarrer la conversation. Vos données
                  ne sont accessibles qu'aux personnes chargées du recrutement.
                </p>
              </FadeUp>
            </div>
            <FadeUp delay={0.15} className="lg:col-span-8">
              <CandidatureForm />
            </FadeUp>
          </div>
        </div>
      </section>
    </main>
  );
}
